define('home/models/Channel',[
    'underscore',

    'core/UniqueModel',
    'core/switches',
    'core/models/Channel',
    'core/utils/auth',
    'core/utils/models',
    'core/strings',

    'home/models/Forum',

    'remote/config',
], function (
    _,

    UniqueModel,
    switches,
    CoreChannel,
    auth,
    modelUtils,
    strings,

    Forum,

    remoteConfig
) {
    'use strict';

    // To be imported lazily to prevent circular dependency
    var CategoryCollection;

    var gettext = strings.get;

    var Channel = CoreChannel.extend({
        // Overwrites core's buildPrimaryForum since home's initializeRelated is used
        buildPrimaryForum: function (options) {
            var forum = options && options.primaryForum || this.get('primaryForum');
            if (_.isEmpty(forum)) {
                this.listenToOnce(this, 'change:primaryForum', _.bind(this.buildPrimaryForum, this, options));
                return;
            }

            if (!Forum)
                Forum = require('home/models/Forum');

            this.initializeRelated('primaryForum', _.defaults({ channel: this }, options), Forum);
            this.trigger('changeRelated:primaryForum');
        },

        getCategories: function () {
            var options = this.get('options');
            var categories = options && options.categories;

            return _.isEmpty(categories) ? null : categories;
        },

        shouldFetch: function () {
            // If channel was created, and then attributes set as the related
            // model for a forum, then name will exist but primaryForum will not.
            // Need to fetch. primaryForum is not initialized by CoreChannel, so
            // it will not have checked for its existance.
            return CoreChannel.prototype.shouldFetch.call(this) || !this.primaryForum;
        },

        /**
         * Most channels allow all users to create. Returns false if this is a curation
         * only channel, or if a whitelist of creators is specified and the user is not
         * in that list.
         * Doesn't check for authentication, because for standard channels we want to show
         * logged out users the option to create discussion, but trying to do so will
         * prompt them to log in.
         *
         * @param {User} user - The User Model to check for creation privileges.
         * @returns {boolean} - Whether or not the given user can create a thread on this
         *                      channel.
         */
        userCanCreate: function (user) {
            // Nobody can create on curation channels
            if (this.get('options').isCurationOnlyChannel)
                return false;

            // Only admins can create on admin channels
            if (switches.isFeatureActive('home_admin_channel', this.attributes))
                return auth.getFromCookie().staff;

            var creatorsWhitelist = remoteConfig.lounge &&
                remoteConfig.lounge.home_channel_creator_whitelists &&
                remoteConfig.lounge.home_channel_creator_whitelists[this.id];

            // Only admins and whitelisted users can create on whitelist channels
            return !creatorsWhitelist ||
                    auth.getFromCookie().staff ||
                    _.contains(creatorsWhitelist, user.get('username'));
        },

        /**
         * A more stringent ensureFetched. Requires not only the standard fetch data
         * but also requies counters, which are not returned in standard transformers
         * but requires a specific api call.
         */
        ensureFetchedWithCounters: function () {
            if (!this.primaryForum || !this.primaryForum.has('numThreads'))
                this.fetch();
        },

        buildFetchData: function (existing) {
            var data = CoreChannel.prototype.buildFetchData.call(this, existing);

            // Only in home make sure to fetch channel stats (counters) and categories
            // when fetching. These are shown on pages specific to one channel, ex:
            // the channel profile page or a channel discussion page.
            return _.extend({
                attach: ['counters', 'channel_categories'],
            }, data);
        },

        validate: function (attrs) {
            var validationErrors = CoreChannel.prototype.validate.call(this, attrs) || [];

            if (!CategoryCollection)
                CategoryCollection = require('home/collections/CategoryCollection');

            var validCategories = CategoryCollection.get().pluck('slug');
            if (!attrs.primaryCategory) {
                validationErrors.push({
                    attrName: 'primaryCategory',
                    message: 'Primary cateogry is required',
                });
            } else if (!_.contains(validCategories, attrs.primaryCategory.slug)) {
                validationErrors.push({
                    attrName: 'primaryCategory',
                    message: gettext('Invalid primary category'),
                });
            }

            if (!_.isEmpty(validationErrors))
                return validationErrors;
        },

        toJSON: function () {
            var json = CoreChannel.prototype.toJSON.call(this);

            return modelUtils.channel.extendJSON(_.defaults({
                primaryForum: this.primaryForum ? this.primaryForum.toJSON() : {},
            }, json));
        },

        follow: function (options) {
            var retval = CoreChannel.prototype.follow.call(this, options);

            var Session = require('home/models/Session');
            var sessionUser = Session.get().user;

            sessionUser.trigger('followed:channel', this);

            return retval;
        },

        unfollow: function (options) {
            var Session = require('home/models/Session');
            var sessionUser = Session.get().user;

            options = options || {};
            options.success = _.bind(function () {
                // Trigger unsubscribing from curation-only channels after success because
                // subscribers may need to refresh certain feeds after this completes.
                if (this.get('options').isCurationOnlyChannel && !this.primaryForum.get('isFollowing'))
                    sessionUser.trigger('unfollowed:curationchannel', this);
            }, this);

            var retval = CoreChannel.prototype.unfollow.call(this, options);

            sessionUser.trigger('unfollowed:channel', this);

            return retval;
        },
    }, {
        getOrCreate: function (attrs, options) {
            var id = attrs[Channel.prototype.idAttribute];
            if (id) {
                var existing = UniqueModel.get(Channel, id);
                if (existing)
                    return existing;
            }

            return new UniqueModel(Channel, attrs, options);
        },
    });

    return Channel;
});

